import { useMemo } from 'react';

import { ALL_EQUIPMENT_LIMIT } from '@/store/equipment/equipment.api';
import {
  useEquipmentCategoriesQuery,
  useEquipmentsQuery,
  useEquipmentTypesQuery,
} from '@/store/equipment/equipment.store';

/**
 * The catalog category the checklist reads from and writes into. Vessel seeds
 * the categories globally and matches them by name, so the name is the only
 * handle the frontend has on this one — there is no id to hard-code.
 */
export const SAFETY_EQUIPMENT_CATEGORY = 'Safety Equipment';

export interface SafetyEquipmentOption {
  id: string;
  name: string;
}

export interface SafetyEquipment {
  /** The vessel's items in the Safety Equipment category, for the picker. */
  options: SafetyEquipmentOption[];
  /**
   * The type an inline-added item is filed under. Vessel requires a `type_id`
   * on every equipment create and the Safety Equipment category has no generic
   * type, so a name-only add takes the category's first type rather than asking
   * the crew for one they were not shown.
   */
  defaultTypeId: string;
  /** Whether the category itself resolved — false means nothing can be added. */
  isReady: boolean;
}

/**
 * The Safety Equipment category as the safety log needs it: what the vessel
 * already has, and what a new piece of it would be filed under.
 *
 * Three requests deep because the catalog is: the category is found by name,
 * the items and the types both hang off its id. All three are the same cached
 * queries the equipment screens use, so a crew member who came from there pays
 * for none of them again.
 */
export function useSafetyEquipment(): SafetyEquipment {
  const { data: categories } = useEquipmentCategoriesQuery(ALL_EQUIPMENT_LIMIT, '');

  const categoryId = useMemo(
    () =>
      categories?.pages
        .flatMap((page) => page.items)
        .find((category) => category.name === SAFETY_EQUIPMENT_CATEGORY)?.id ?? '',
    [categories],
  );

  const { data: equipment } = useEquipmentsQuery(categoryId, ALL_EQUIPMENT_LIMIT, '');
  const { data: types } = useEquipmentTypesQuery(categoryId);

  const options = useMemo(
    () =>
      equipment?.pages
        .flatMap((page) => page.items)
        .map(({ id, name }) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name)) ?? [],
    [equipment],
  );

  return {
    options,
    defaultTypeId: types?.items[0]?.id ?? '',
    isReady: !!categoryId,
  };
}
